/**
 * DPDP consent capture (S7-7; XC-01 · IFSCA-05 · INV-6).
 *
 * The droom fetches the active, versioned consent text for the session's regime,
 * shows the customer the purposes plainly, and — on an explicit affirmative
 * action — posts an immutable PRE_JOIN consent record. This replaces the as-built
 * bare `acceptedTnC` boolean with a real, hash-anchored, versioned record.
 *
 * Server-authoritative version stamp: the client echoes back the exact
 * `consentVersion` it displayed; the backend cross-checks it against its own
 * active text (a mismatch means the notice changed under the customer → 422,
 * re-affirm). The source IP is derived server-side from the edge chain — the
 * client never sends it (client.ts security posture), exactly like geo.
 */

import { isMockMode, request } from './client';

/** The four DPDP processing purposes the affirmation must cover (canonical keys). */
export type ConsentPurposeKey =
  | 'RECORDED_VIDEO'
  | 'BIOMETRIC_LIVENESS'
  | 'LOCATION'
  | 'DATA_RETENTION'
  | 'FRAUD_DEDUP_SCREENING';

/** The active consent text for a regime (carries the UNRATIFIED stamp inline). */
export interface ConsentText {
  regime: string;
  consentVersion: string;
  /** false while the version carries the -UNRATIFIED marker (VCIP-REG has not ratified the wording). */
  ratified: boolean;
  purposes: ConsentPurposeKey[];
  noticeBody: string;
}

export interface ConsentCaptureBody {
  consentVersion: string;
  method: 'PRE_JOIN' | 'IN_PRESENCE';
  acceptedPurposes: ConsentPurposeKey[];
}

/** The immutable consent record echoed by the backend (never carries the raw source IP). */
export interface ConsentRecord {
  id: string;
  sessionId: string;
  method: 'PRE_JOIN' | 'IN_PRESENCE';
  consentVersion: string;
  acceptedPurposes: string[];
  acceptedAt: string;
  ipCountry: string | null;
  attestedBy: string | null;
  sha256: string;
}

/**
 * A local, honest fallback used ONLY in mock mode (no backend). Data-minimising:
 * nothing is posted; the customer still sees the real ceremony and affirms, but
 * no evidence rail exists to bind a record to. The stamp is shown as UNRATIFIED.
 */
const MOCK_TEXT: ConsentText = {
  regime: 'RBI',
  consentVersion: 'consent-v1-UNRATIFIED',
  ratified: false,
  purposes: ['RECORDED_VIDEO', 'BIOMETRIC_LIVENESS', 'LOCATION', 'DATA_RETENTION'],
  noticeBody:
    'To complete your Video-KYC verification we process a recorded video of this call, your face ' +
    'and liveness matched to your ID, your live location, and your identity data retained per the ' +
    'applicable retention period. [Preview — wording pending VCIP-REG ratification.]',
};

/** GET the active consent text for the session's regime. Mock mode returns a local fixture. */
export async function getConsentText(sessionId: string): Promise<ConsentText> {
  if (isMockMode()) return MOCK_TEXT;
  return request<ConsentText>(
    `/api/v1/sessions/${encodeURIComponent(sessionId)}/consent/text`,
  );
}

/**
 * POST the consent affirmation. Mock mode (no backend) resolves null WITHOUT
 * posting — no evidence rail exists to bind a record to (data minimisation).
 */
export async function postConsent(
  sessionId: string,
  body: ConsentCaptureBody,
): Promise<ConsentRecord | null> {
  if (isMockMode()) return null;
  return request<ConsentRecord>(
    `/api/v1/sessions/${encodeURIComponent(sessionId)}/consent`,
    { method: 'POST', body },
  );
}
